import { useState, useEffect } from 'react';
import { siteSettingsService, SiteSettings } from '../services/siteSettings';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// Site ayarları hook - Frontend için (read-only)
export const useSiteSettings = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

    useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupListener = async () => {
      try {
        setLoading(true);
        setError(null);

        // İlk yükleme
        const initialSettings = await siteSettingsService.get();
        setSettings(initialSettings);

        // Real-time listener
        const settingsDocRef = doc(db, 'siteSettings', 'main');
        unsubscribe = onSnapshot(settingsDocRef, (doc) => {
          if (doc.exists()) {
            const updatedSettings = doc.data() as SiteSettings;
            setSettings(updatedSettings);
          }
        }, (error) => {
          console.error('❌ Site ayarları listener hatası:', error);
          setError('Ayarlar dinlenirken hata oluştu');
        });

      } catch (err: any) {
        console.error('❌ Site ayarları hook hatası:', err);
        setError(err.message || 'Site ayarları yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    setupListener();

    // Cleanup
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return {
    settings,
    loading,
    error
  };
};

// Admin site ayarları hook - Admin panel için (read-write)
export const useAdminSiteSettings = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedSettings = await siteSettingsService.get();
      setSettings(fetchedSettings);
    } catch (err: any) {
      console.error('❌ Admin site ayarları hatası:', err);
      setError(err.message || 'Site ayarları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Site ayarlarını güncelle
  const updateSettings = async (updates: Partial<SiteSettings>) => {
    try {
      await siteSettingsService.update(updates);
      await fetchSettings(); // Ayarları yeniden yükle
    } catch (err: any) {
      setError(err.message || 'Site ayarları güncellenirken hata oluştu');
      throw err;
    }
  };

  // Slider resmi ekle
  const addSliderImage = async (image: Omit<SiteSettings['sliderImages'][0], 'id'>) => {
    try {
      await siteSettingsService.addSliderImage(image);
      await fetchSettings(); // Ayarları yeniden yükle
    } catch (err: any) {
      setError(err.message || 'Slider resmi eklenirken hata oluştu');
      throw err;
    }
  };

  // Slider resmi sil
  const removeSliderImage = async (imageId: string) => {
    try {
      await siteSettingsService.removeSliderImage(imageId);
      await fetchSettings(); // Ayarları yeniden yükle
    } catch (err: any) {
      setError(err.message || 'Slider resmi silinirken hata oluştu');
      throw err;
    }
  };

  // Quick link ekle
  const addQuickLink = async (link: { title: string; url: string }) => {
    try {
      await siteSettingsService.addQuickLink(link);
      await fetchSettings(); // Ayarları yeniden yükle
    } catch (err: any) {
      setError(err.message || 'Hızlı link eklenirken hata oluştu');
      throw err;
    }
  };

  // Galeri resmi ekle
  const addGalleryImage = async (image: Omit<SiteSettings['gallery'][0], 'id' | 'createdAt'>) => {
    try {
      await siteSettingsService.addGalleryImage(image);
      await fetchSettings(); // Ayarları yeniden yükle
    } catch (err: any) {
      setError(err.message || 'Galeri resmi eklenirken hata oluştu');
      throw err;
    }
  };

  // Galeri resmi sil
  const removeGalleryImage = async (imageId: string) => {
    try {
      await siteSettingsService.removeGalleryImage(imageId);
      await fetchSettings(); // Ayarları yeniden yükle
    } catch (err: any) {
      setError(err.message || 'Galeri resmi silinirken hata oluştu');
      throw err;
    }
  };

  return {
    settings,
    loading,
    error,
    refetch: fetchSettings,
    updateSettings,
    addSliderImage,
    removeSliderImage,
    addQuickLink,
    addGalleryImage,
    removeGalleryImage
  };
};